import { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  aromaDraggableItem,
  AromaDraggableItemVariantProps,
} from "@zenchef/styled-system/recipes";
import React, { useEffect, useState } from "react";
import { DraggableItemData } from "./DraggableItem";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

type DragStateType = "idle" | "dragging" | "dragging-over" | "preview";
interface DragState {
  type: DragStateType;
  closestEdge?: Edge | null;
  container?: HTMLDivElement | null;
}
const useDragAndDrop = ({
  id,
  itemRef,
  dragHandleRef,
  disabled,
  variantProps,
  flow,
}: {
  id: string | number;
  itemRef: React.RefObject<HTMLElement>;
  dragHandleRef: React.RefObject<HTMLElement>;
  disabled?: boolean;
  variantProps: AromaDraggableItemVariantProps;
  flow: string;
}): DragState => {
  const [dragState, setDragState] = useState<DragState>({
    type: "idle",
  });

  // Configuring DraggableItem as draggable element
  const handleDraggable = ({
    element,
    dragHandle,
  }: {
    element: HTMLElement;
    dragHandle: HTMLElement;
  }) =>
    draggable({
      element,
      dragHandle,
      getInitialData() {
        return { id } satisfies DraggableItemData;
      },
      // Function to change the appearance of the draggable item when it is being dragged
      // This is a workaround for the issue described in the Atlassian Design System documentation:
      // https://atlassian.design/components/pragmatic-drag-and-drop/core-package/adapters/element/drag-previews#approach-2-change-the-appearance-of-a-draggable
      onGenerateDragPreview({ source }) {
        const addMissingClasses = (
          el: Element,
          classNameString: string,
        ): string[] => {
          const missingClasses = classNameString
            .split(" ")
            .filter((className) => !el.classList.contains(className));
          missingClasses.forEach((className) => el.classList.add(className));
          return missingClasses;
        };

        const activeClasses = aromaDraggableItem({
          ...variantProps,
          active: true,
        });

        // Adding "active" classes to the draggable item wrapper and knob so preview appears as active
        const activeWrapperClasses = addMissingClasses(
          source.element,
          activeClasses.wrapper,
        );

        const knobElement = source.element.querySelector(`[data-part="knob"]`)!;
        const activeKnobClasses = addMissingClasses(
          knobElement,
          activeClasses.knob,
        );

        // Removing "active" classes right after so element itself has default appearance
        requestAnimationFrame(() => {
          activeWrapperClasses.forEach((className) =>
            source.element.classList.remove(className),
          );
          activeKnobClasses.forEach((className) =>
            knobElement.classList.remove(className),
          );
        });
      },
      onDrag() {
        setDragState({
          type: "dragging",
        });
      },
      onDrop() {
        setDragState({
          type: "idle",
        });
      },
      canDrag() {
        return !disabled;
      },
    });

  // Configuring DraggableItem as a drop target
  const handleDropTargetForElements = ({ element }: { element: HTMLElement }) =>
    dropTargetForElements({
      element,
      getData({ input }) {
        const data = { id } satisfies DraggableItemData;
        return attachClosestEdge(data, {
          element,
          input,
          allowedEdges:
            flow === "vertical" ? ["top", "bottom"] : ["left", "right"],
        });
      },
      // Determines whether drop target stays active when dragging over non-target area
      // e.g. drop target should be the last element if dropping over the bottom of the list
      getIsSticky() {
        return true;
      },
      // Function to show signifier at the edge of the target
      onDragEnter({ self, source }) {
        if (source.data.id === self.data.id) return;
        const closestEdge = extractClosestEdge(self.data);
        setDragState({
          type: "dragging-over",
          closestEdge,
        });
      },
      onDrag({ self, source }) {
        if (source.data.id === self.data.id) return;
        const closestEdge = extractClosestEdge(self.data);
        setDragState((current) => {
          if (
            current.type === "dragging-over" &&
            current.closestEdge === closestEdge
          ) {
            return current;
          }
          return { type: "dragging-over", closestEdge };
        });
      },
      // Function to hide signifier at the edge of the target (but not change "dragging" state when dragging by itself
      onDragLeave({ self, source }) {
        if (source.data.id === self.data.id) return;
        setDragState({ type: "idle" });
      },
      onDrop() {
        setDragState({ type: "idle" });
      },
    });

  useEffect(() => {
    const element = itemRef.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;
    // DraggableItem can serve as both draggable element and drop target
    return combine(
      handleDraggable({ element, dragHandle }),
      handleDropTargetForElements({ element }),
    );
  }, [disabled, flow]);

  return dragState;
};

export default useDragAndDrop;
